import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShopLayout } from "@/components/ShopLayout";
import { cartTotal, loadCart, saveCart, type CartLine } from "@/lib/cart";
import { formatDate, formatPrice } from "@/lib/format";
import { api } from "@/lib/api";

export function CartPage() {
  const navigate = useNavigate();
  const [lines, setLines] = useState<CartLine[]>(() => loadCart());
  const [checkoutEnabled, setCheckoutEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    void api
      .get<{ checkout_enabled: boolean }>("/api/v1/public/shop/config", { auth: false })
      .then((cfg) => setCheckoutEnabled(cfg.checkout_enabled))
      .catch(() => setCheckoutEnabled(false));
  }, []);

  function updateQuantity(articleId: string, delta: number) {
    const next = lines
      .map((line) =>
        line.articleId === articleId
          ? { ...line, quantity: Math.max(0, line.quantity + delta) }
          : line,
      )
      .filter((line) => line.quantity > 0);
    saveCart(next);
    setLines(next);
  }

  function removeLine(articleId: string) {
    const next = lines.filter((line) => line.articleId !== articleId);
    saveCart(next);
    setLines(next);
  }

  const total = cartTotal(lines);

  return (
    <ShopLayout>
      <div className="shop-page mx-auto max-w-2xl space-y-6">
        <header>
          <h1 className="shop-page-title">Warenkorb</h1>
          <Link to="/" className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:text-brand-800">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
            Weiter einkaufen
          </Link>
        </header>

        {lines.length === 0 ? (
          <div className="shop-card flex flex-col items-center gap-4 py-16 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
              <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z"
                />
              </svg>
            </div>
            <p className="text-sm text-slate-600">Ihr Warenkorb ist leer.</p>
            <Link to="/" className="shop-btn-primary">
              Zum Shop
            </Link>
          </div>
        ) : (
          <>
            <ul className="space-y-3">
              {lines.map((line) => (
                <li key={line.articleId} className="shop-card">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900">{line.name}</p>
                      {line.requiresPickupReceipt && line.pickupDate ? (
                        <p className="mt-1 flex items-center gap-1.5 text-xs text-amber-700">
                          <span className="shop-badge-pickup !px-2 !py-0">Abholung</span>
                          {formatDate(line.pickupDate)}
                        </p>
                      ) : null}
                      <p className="mt-1 text-sm tabular-nums text-slate-500">
                        {formatPrice(line.priceGross)} je Stück
                      </p>
                    </div>
                    <p className="shrink-0 text-lg font-bold tabular-nums text-slate-900">
                      {formatPrice(line.priceGross * line.quantity)}
                    </p>
                  </div>

                  <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4">
                    <div className="flex items-center gap-1 rounded-xl bg-slate-50 p-1">
                      <button
                        type="button"
                        className="flex h-9 w-9 items-center justify-center rounded-lg text-lg text-slate-600 transition hover:bg-white hover:shadow-sm"
                        onClick={() => updateQuantity(line.articleId, -1)}
                        aria-label="Menge verringern"
                      >
                        −
                      </button>
                      <span className="w-10 text-center text-sm font-semibold tabular-nums">
                        {line.quantity}
                      </span>
                      <button
                        type="button"
                        className="flex h-9 w-9 items-center justify-center rounded-lg text-lg text-slate-600 transition hover:bg-white hover:shadow-sm"
                        onClick={() => updateQuantity(line.articleId, 1)}
                        aria-label="Menge erhöhen"
                      >
                        +
                      </button>
                    </div>
                    <button
                      type="button"
                      className="text-sm font-medium text-red-600 hover:text-red-700"
                      onClick={() => removeLine(line.articleId)}
                    >
                      Entfernen
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            <div className="shop-card flex items-center justify-between bg-brand-50/50">
              <span className="font-medium text-slate-700">Gesamt (brutto)</span>
              <span className="text-2xl font-bold tabular-nums text-slate-900">{formatPrice(total)}</span>
            </div>

            {checkoutEnabled === false ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                Online-Zahlung ist noch nicht aktiv. Bitte bestellen Sie vorerst an der Rezeption.
              </div>
            ) : null}

            <button
              type="button"
              disabled={checkoutEnabled === false}
              onClick={() => navigate("/kasse")}
              className="shop-btn-primary w-full"
            >
              Zur Kasse
            </button>
          </>
        )}
      </div>
    </ShopLayout>
  );
}
